import { withTranslation } from "next-i18next";
import { useState } from "react";
import { useRouter } from "next/router";
import ahoy from "@lib/ahoy";
import gtm from "@lib/gtm";
import Bugsnag from "@lib/bugsnag";
import { useCatalogVersion } from "@hooks/use-features";
import { useOpenCartModal, usePostCartItem } from "@hooks/use-cart";
import { useCurrentUser } from "@hooks/use-current-user";
import { useOpenDrawer } from "@hooks/use-drawer";
import useCustomizableCloset, {
  useAddSelectedItemToCloset,
  usePutSelectedItemInCloset,
} from "@hooks/use-customizable-closet";
import AuthOffcanvas from "@components/core/AuthOffcanvas";
import Button from "@components/core/Button";
import logger from "@utils/logger";
import { productAddedEvent } from "@utils/events/product-events";
import { showErrorToast, showSuccessToast } from "@utils/toasts/showToasts";
import catalogContexts from "@utils/constants/catalogContexts";
import closetStates from "@utils/constants/closetStates";
import { animateItemCounter } from "@utils/animations";
import ProductVariants from "./ProductVariants";
import ProductRecommendations from "./ProductRecommendations";

const ProductAction = ({ product, context, t }) => {
  const router = useRouter();
  const user = useCurrentUser();
  const openDrawer = useOpenDrawer();
  const openCartModal = useOpenCartModal();
  const postCartItem = usePostCartItem();
  const currentCloset = useCustomizableCloset();
  const addSelectedItemToCloset = useAddSelectedItemToCloset();
  const putSelectedItemInCloset = usePutSelectedItemInCloset();
  const catalogVersion = useCatalogVersion();

  const [loading, setLoading] = useState(false);
  const [authOffcanvasOpen, setAuthOffcanvasOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const getSelectedItem = () => {
    if (!currentCloset) return null;
    if (currentCloset.state !== closetStates.readyToChange) return null;

    return currentCloset.closet_items?.find((ci) => ci.product.id === product.id);
  };

  const isSwapContext = context === catalogContexts.swap;
  const closetItemInCloset = getSelectedItem();
  const variantAlreadyInCloset =
    selectedVariant && closetItemInCloset
      ? selectedVariant.id === closetItemInCloset.variant.id
      : false;
  const unavailable = product.variants?.every((variant) => !variant.available);
  const disabled =
    !selectedVariant || loading || (variantAlreadyInCloset && isSwapContext) || unavailable;

  function handleClosetError(err) {
    const error = err.response.data;
    const errorCode = error.code ? error.code : "unknown";
    const errorTitle = t(`common:customize_closet.errors.${errorCode}.title`);
    const errorMessage = t(`common:customize_closet.errors.${errorCode}.message`);

    showErrorToast(errorTitle, errorMessage);

    ahoy("drawerError", {
      closetId: currentCloset?.id,
      variantSelectedId: selectedVariant?.id,
    });
    Bugsnag.notify(new Error("Failed to add selected item to Closet"), (event) => {
      event.context = "DrawerError";
      event.severity = "info";
      event.setUser(user);
      event.addMetadata("params", {
        closetId: currentCloset?.id,
        variantSelectedId: selectedVariant?.id,
      });
    });
  }

  function sendProductAddedEvent() {
    const objectID =
      catalogVersion === 1
        ? product.variants.find((variant) => variant.id === selectedVariant?.id).uid
        : product.uid;
    const dataToSend = {
      index: router.query?.algoliaIndexName,
      objectID,
      queryID: router.query?.algoliaQueryId,
    };

    productAddedEvent(dataToSend, "productAddedFromProductPage");
  }

  async function customizeChangeVariant() {
    setLoading(true);

    try {
      await putSelectedItemInCloset(closetItemInCloset?.id, selectedVariant?.id);
      showSuccessToast(
        t("common:customize_closet.success.modified.title"),
        t("common:customize_closet.success.modified.message"),
      );
    } catch (err) {
      handleClosetError(err);
    } finally {
      setLoading(false);
    }
  }

  function allowAddItemToCloset() {
    if (currentCloset?.state === "ready_to_change") {
      return true;
    } else {
      openDrawer();
      return false;
    }
  }

  async function customizeAddSelectedItem() {
    if (!allowAddItemToCloset()) return;

    setLoading(true);

    try {
      await addSelectedItemToCloset(selectedVariant?.id);
      animateItemCounter(".drawer-icon-counter");
      sendProductAddedEvent();
    } catch (err) {
      handleClosetError(err);
    } finally {
      setLoading(false);
    }
  }

  function sendAddToCartGtm(cart) {
    try {
      gtm(
        {
          event: "addToCart",
          ecommerce: {
            items: cart?.cart_items.map((cartItem) => {
              const cartItemProduct = cartItem.product;
              return {
                item_brand: cartItemProduct.brand.name,
                item_name: cartItemProduct.name,
                item_id: cartItemProduct.uid,
                item_price: cartItemProduct.current_price,
                item_category: cartItemProduct.category,
              };
            }),
          },
        },
        user,
      );
    } catch (err) {
      logger.error("Error while sending gtm event addToCart", err, cart);
    }
  }

  async function addToCart() {
    if (!selectedVariant?.id) return;

    try {
      setLoading(true);
      const currentCart = await postCartItem(selectedVariant?.id);

      sendAddToCartGtm(currentCart);
      animateItemCounter(".cart-icon-counter");
      openCartModal();
    } catch (err) {
      const errorCode = err?.data?.code || "unknown";
      showErrorToast(
        t(`product:add_cart_errors.${errorCode}.title`),
        t(`product:add_cart_errors.${errorCode}.message`),
      );
    } finally {
      setLoading(false);
    }
  }

  function addToCartAfterAuthAction() {
    setAuthOffcanvasOpen(false);
    addToCart();
  }

  async function authAndOrAddToCart() {
    if (!user) {
      gtm({
        event: "unsignedAddToCart",
      });

      setAuthOffcanvasOpen(true);
      return;
    }

    addToCart();
  }

  const displayTextActionBtn = () => {
    switch (context) {
      case catalogContexts.swap:
        if (variantAlreadyInCloset) return t("product:in_your_closet");
        if (closetItemInCloset && !unavailable) return t("product:swap_size");

        return t("product:add_to_box");
      case catalogContexts.sales:
        return t("product:add_to_cart");
      default:
        return t("product:add_to_box");
    }
  };

  function rentOrBuy() {
    if (context === catalogContexts.sales) {
      authAndOrAddToCart();
      return;
    }

    if (!closetItemInCloset) {
      customizeAddSelectedItem();
      return;
    }

    customizeChangeVariant();
  }

  const errorText = () => {
    switch (context) {
      case catalogContexts.swap:
        if (!closetItemInCloset && unavailable) return t("product:unavailable");
        if (!selectedVariant) return t("product:select_size_closet");

        return "";

      case catalogContexts.sales:
        if (unavailable) return t("product:unavailable");
        if (!selectedVariant) return t("product:select_size_closet");

        return "";
      default:
        return "";
    }
  };

  return (
    <div>
      <AuthOffcanvas
        open={authOffcanvasOpen}
        initType="signup"
        userAction="addToCart"
        onHide={() => setAuthOffcanvasOpen(false)}
        onAfterAuth={addToCartAfterAuthAction}
      />

      <ProductVariants
        product={product}
        closetItemInCloset={closetItemInCloset}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        context={context}
      />

      <div style={{ maxWidth: "500px" }}>
        <ProductRecommendations product={product} />

        <Button
          trackingName={isSwapContext ? "productPageAddToCloset" : "productPageAddToCart"}
          onClick={() => rentOrBuy()}
          variant={isSwapContext ? "primary" : "tertiary"}
          fullWidth
          disabled={disabled}
          loading={loading}
        >
          {displayTextActionBtn()}
        </Button>

        {errorText() && <p className="mb-0 py-2 text-primary font-size-sm">{errorText()}</p>}
      </div>
    </div>
  );
};

export default withTranslation("product")(ProductAction);
